import React, { useState } from 'react'
import { FaBullhorn, FaCalendarAlt, FaHeading, FaFileAlt } from 'react-icons/fa'
import { motion } from 'framer-motion'
import { sendApiRequest } from '../utils/api'

const AddNotice = () => {
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    content: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')

    try {
      const response = await sendApiRequest('api/admin/notices', formData, 'POST')
      if (response.status === false) throw new Error(response.message)

      setSuccess('Notice published successfully!')
      setFormData({ title: '', date: '', content: '' })
    } catch (err) {
      setError(err.message || 'Error publishing notice')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  return (
    <div className="p-4 sm:p-6 md:p-8 max-w-4xl mx-auto bg-gradient-to-br from-white to-blue-50 min-h-screen">
      {/* Header */}
      <div className="mb-6 sm:mb-8 md:mb-10 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 rounded-full mb-3 sm:mb-4 border border-blue-200 shadow">
          <FaBullhorn className="text-blue-600 w-6 h-6 sm:w-8 sm:h-8" />
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">Create New Notice</h1>
        <p className="text-sm sm:text-base text-gray-500">Share important updates with your students</p>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-blue-100">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaHeading className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter notice title"
              className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-3 sm:py-4 rounded-lg sm:rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition bg-blue-50 text-gray-800 placeholder-gray-400"
              required
            />
          </div>

          {/* Date */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaCalendarAlt className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-3 sm:py-4 rounded-lg sm:rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition bg-blue-50 text-gray-800"
              required
            />
          </div>

          {/* Content */}
          <div className="relative">
            <div className="absolute top-3 left-0 pl-3 flex items-center pointer-events-none">
              <FaFileAlt className="h-5 w-5 text-gray-400" />
            </div>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              className="min-h-[150px] sm:min-h-[200px] w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-3 sm:py-4 rounded-lg sm:rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition resize-none bg-blue-50 text-gray-800 placeholder-gray-400"
              placeholder="Enter notice content"
              required
            />
          </div>

          {/* Messages */}
          {error && (
            <div className="p-3 sm:p-4 bg-red-100 border border-red-400 rounded-lg sm:rounded-xl text-red-700">
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 sm:p-4 bg-green-100 border border-green-400 rounded-lg sm:rounded-xl text-green-700">
              {success}
            </div>
          )}

          {/* Buttons */}
          <div className="pt-4 flex flex-col sm:flex-row gap-2 sm:gap-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="button"
              onClick={() => setFormData({ title: '', date: '', content: '' })}
              className="py-3 sm:py-4 px-4 sm:px-6 rounded-lg sm:rounded-xl font-medium border border-gray-300 text-gray-600 hover:bg-gray-100 transition w-full sm:w-auto"
            >
              Cancel
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              className="py-3 sm:py-4 px-4 sm:px-6 rounded-lg sm:rounded-xl font-medium bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
            >
              {loading ? 'Publishing...' : 'Publish Notice'}
            </motion.button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddNotice
